import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { ShopsActions } from './shops.actions';
import { NonNullDynamicState, SelectorUtil, Shop } from '@us/web/shared/data-access';
import { first } from 'lodash';

export const SHOPS_FEATURE_KEY = 'shops';

export interface ShopsState extends NonNullDynamicState<'shops', Shop[]> {
    selectedShopId: string | null;
}

export const initialShopsState: ShopsState = {
    status: 'pending',
    shops: [],
    selectedShopId: null,
    error: null
};

const reducer = createReducer(
    initialShopsState,
    on(ShopsActions.loadShops, (state): ShopsState => ({ ...state, status: 'loading', error: null })),
    on(ShopsActions.loadShopsSuccess, (state, { shops }): ShopsState => {
        const selectedShopId =
            state.selectedShopId && shops?.findIndex(shop => shop.id === state.selectedShopId) > -1
                ? state.selectedShopId
                : first(shops)?.id || null;

        return {
            ...state,
            status: 'success',
            shops: shops || [],
            selectedShopId,
            error: null
        };
    }),
    on(ShopsActions.loadShopsFailure, (state, { error }): ShopsState => ({ ...state, error, status: 'error' })),
    on(ShopsActions.selectShop, (state, { shopId }): ShopsState => ({ ...state, selectedShopId: shopId }))
);

export const shopsFeature = createFeature({
    name: SHOPS_FEATURE_KEY,
    reducer
});

export const { selectShopsState, selectShops, selectStatus, selectError, selectSelectedShopId } = shopsFeature;

export const selectShopsIsDone = createSelector(selectShopsState, SelectorUtil.isDone);
export const selectShopsIsLoading = createSelector(selectShopsState, SelectorUtil.isLoading);

export const selectShop = (id: string) =>
    createSelector(selectShops, stores => stores.find(store => store.id === id) || null);

export const selectSelectedShop = createSelector(
    selectShops,
    selectSelectedShopId,
    (shops, id) => shops.find(shop => shop.id === id) || null
);
